import React, { useRef } from 'react';

/**
 * NumberPlateGenerator - A reusable number plate component
 * @param {Object} props
 * @param {Object} props.config - Plate configuration
 * @param {string} props.config.format_pattern - Pattern like "L 12345"
 * @param {string} props.config.color_hex - Background color
 * @param {string} props.config.text_color_hex - Text color
 * @param {string} props.config.border_color_hex - Border color
 * @param {string} props.config.emblem - Top emblem text
 * @param {string} props.config.font_name - Font family
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 * @param {boolean} props.downloadable - Show download button
 * @param {Function} props.onDownload - Custom download handler
 */
const NumberPlateGenerator = ({ 
  config = {},
  className = '',
  style = {},
  downloadable = false,
  onDownload = null
}) => {
  const plateRef = useRef(null);

  const {
    format_pattern = "L 12345",
    color_hex = "#FFFFFF",
    text_color_hex = "#000000",
    border_color_hex = "#000000",
    emblem = "",
    font_name = "Arial"
  } = config;

  // Parse the format pattern to separate letters and numbers
  const parsePattern = (pattern) => {
    const parts = pattern.split(/[\s-]/);
    const letters = parts.find(p => /^[A-Z]+$/.test(p)) || "A";
    const numbers = parts.find(p => /^\d+$/.test(p)) || "12345";
    return { letters, numbers };
  };

  const { letters, numbers } = parsePattern(format_pattern);

  // Download functionality
  const handleDownload = async () => {
    if (onDownload) {
      onDownload(plateRef.current);
      return;
    }

    // Default download using canvas (if html2canvas is available)
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(plateRef.current, {
        backgroundColor: null,
        scale: 2
      });
      
      const link = document.createElement('a');
      link.download = `number-plate-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Download failed. Install html2canvas: npm install html2canvas', error);
      alert('Download feature requires html2canvas library');
    }
  };

  return (
    <div className={`number-plate-wrapper ${className}`} style={style}>
      <div
        ref={plateRef}
        className="number-plate"
        style={{
          backgroundColor: color_hex,
          border: `3px solid ${border_color_hex}`,
          borderRadius: "8px",
          display: "inline-flex",
          alignItems: "stretch",
          minWidth: "200px",
          height: "60px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          overflow: "hidden"
        }}
      >
        {/* Left Section - Emblem and Letter */}
        <div
          className="plate-left"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "4px 12px",
            minWidth: "60px"
          }}
        >
          {/* Emblem on top */}
          {emblem && (
            <div
              className="plate-emblem"
              style={{
                fontSize: "10px",
                fontWeight: "600",
                color: text_color_hex,
                marginBottom: "2px",
                textAlign: "center",
                lineHeight: "1"
              }}
            >
              {emblem}
            </div>
          )}
          {/* Letter below emblem */}
          <div
            className="plate-letter"
            style={{
              fontFamily: font_name || "Arial",
              fontSize: "22px",
              fontWeight: "bold",
              color: text_color_hex,
              lineHeight: "1"
            }}
          >
            {letters}
          </div>
        </div>

        {/* Right Section - Numbers */}
        <div
          className="plate-numbers"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            padding: "0 16px",
            fontFamily: font_name || "Arial",
            fontSize: "28px",
            fontWeight: "bold",
            color: text_color_hex,
            letterSpacing: "2px"
          }}
        >
          {numbers}
        </div>
      </div>

      {downloadable && (
        <button
          onClick={handleDownload}
          className="download-btn"
          style={{
            marginTop: "8px",
            padding: "6px 12px",
            backgroundColor: "#475EAB",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "12px"
          }}
        >
          📥 Download Plate
        </button>
      )}
    </div>
  );
};

// Demo Component
const Demo = () => {
  const demoConfigs = [
    {
      format_pattern: "L 12345",
      color_hex: "#FFFFFF",
      text_color_hex: "#000000",
      border_color_hex: "#000000",
      emblem: "UAE",
      font_name: "Arial Black"
    },
    {
      format_pattern: "ABC 9999",
      color_hex: "#FFD700",
      text_color_hex: "#000000",
      border_color_hex: "#FF0000",
      emblem: "GOLD",
      font_name: "Arial"
    },
    {
      format_pattern: "XYZ 7777",
      color_hex: "#000000",
      text_color_hex: "#FFFFFF",
      border_color_hex: "#FFD700",
      emblem: "VIP",
      font_name: "Courier New"
    }
  ];

  return (
    <div style={{ padding: "40px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <h1 style={{ textAlign: "center", marginBottom: "40px", color: "#333" }}>
          🚗 Number Plate Generator Library
        </h1>

        <div style={{ 
          backgroundColor: "white", 
          padding: "30px", 
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          marginBottom: "30px"
        }}>
          <h2 style={{ marginBottom: "20px", color: "#475EAB" }}>Installation</h2>
          <pre style={{ 
            backgroundColor: "#f8f9fa", 
            padding: "15px", 
            borderRadius: "8px",
            overflow: "auto"
          }}>
            <code>{`// Copy NumberPlateGenerator component to your project
import NumberPlateGenerator from './components/NumberPlateGenerator';

// Usage
<NumberPlateGenerator 
  config={{
    format_pattern: "L 12345",
    color_hex: "#FFFFFF",
    text_color_hex: "#000000",
    border_color_hex: "#000000",
    emblem: "UAE",
    font_name: "Arial Black"
  }}
  downloadable={true}
/>`}</code>
          </pre>
        </div>

        <div style={{ 
          backgroundColor: "white", 
          padding: "30px", 
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
        }}>
          <h2 style={{ marginBottom: "20px", color: "#475EAB" }}>Examples</h2>
          
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "30px",
            marginTop: "20px"
          }}>
            {demoConfigs.map((config, index) => (
              <div key={index} style={{ 
                padding: "20px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                textAlign: "center"
              }}>
                <h3 style={{ marginBottom: "15px", fontSize: "16px", color: "#666" }}>
                  Style {index + 1}
                </h3>
                <NumberPlateGenerator 
                  config={config}
                  downloadable={true}
                />
              </div>
            ))}
          </div>
        </div>

        <div style={{ 
          backgroundColor: "#475EAB", 
          color: "white",
          padding: "30px", 
          borderRadius: "12px",
          marginTop: "30px",
          textAlign: "center"
        }}>
          <h2 style={{ marginBottom: "15px" }}>Props API</h2>
          <ul style={{ 
            listStyle: "none", 
            padding: 0,
            textAlign: "left",
            maxWidth: "600px",
            margin: "0 auto"
          }}>
            <li style={{ marginBottom: "10px" }}>✅ <strong>config</strong>: Plate configuration object</li>
            <li style={{ marginBottom: "10px" }}>✅ <strong>downloadable</strong>: Show download button</li>
            <li style={{ marginBottom: "10px" }}>✅ <strong>className</strong>: Additional CSS classes</li>
            <li style={{ marginBottom: "10px" }}>✅ <strong>style</strong>: Additional inline styles</li>
            <li style={{ marginBottom: "10px" }}>✅ <strong>onDownload</strong>: Custom download handler</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Demo;
export { NumberPlateGenerator };